import { resolveRowLabels } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/rowLabels';
import {
  ComparisonTableProduct,
  ComponentComparisonTable,
} from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/types';
import { withVerticalScope } from '@hyphengroup/cms-components/utils/cmsPageContext';
import classNames from 'classnames';
import React from 'react';
import { BrandId } from '../../../../utils/override';
import { ComparisonTableWrapper } from '../components-internal/ComparisonTable/ComparisonTableWrapper';
import { getLoanSearchParamsWithDefaultComponentParam } from '../helpers/loanSearchParams';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { ComponentProductMetadata } from '$/(cms)/_modules/cmsService/types';
import { ProductComparisonTable } from '$/components/cms/components-internal/ProductComparisonTable';
import { getProductBlockVertical } from '$/components/cms/helpers/productBlockVertical';
import { VerticalType } from '$/components/cms/types';

type ComparisonTableMapProps = {
  tableConfig: ComponentComparisonTable;
  metadata?: ComponentProductMetadata;
  vertical?: VerticalType;
  slug?: string;
  brand: BrandId;
  blockIndex?: number;
};

const ComparisonTableMapSwitch: React.FC<ComparisonTableMapProps> = ({
  tableConfig,
  metadata,
  vertical,
  slug,
  brand,
  blockIndex,
}) => {
  const { pageType } = usePageDataContext();
  // Only entries that render a row take part in the label cap, in ProductMetaConfig order and
  // before tabs/filters/sort, so the user's view state cannot change who is labelled (CMS-1906).
  const cards = (metadata?.product_card ?? []).flatMap((card) => {
    const productBlock = card.productBlock;
    return productBlock?.product && card.visible !== false
      ? [{ card, productBlock, product: productBlock.product }]
      : [];
  });
  const rowLabels = resolveRowLabels(cards.map(({ card }) => card));
  const products: ComparisonTableProduct[] = cards.map(
    ({ productBlock, product }, index) => ({
      ...product,
      tab: productBlock.tab,
      hot_reward: productBlock.hot_reward,
      override_cta_label: productBlock.override_cta_label_short,
      override_cta_link: productBlock.override_cta_link,
      attributes_from_meta: productBlock.attributes_list,
      e2eToogleButton: productBlock.travelInsurance?.e2eToggleButton,
      row_label: rowLabels[index],
    }),
  );

  const mobileVisible = classNames({
    'hidden md:block': !tableConfig.visible_mobile,
  });

  const loanSearchParams = getLoanSearchParamsWithDefaultComponentParam(
    brand,
    undefined,
    {
      amount: tableConfig.default_loan_amount?.toString(),
      tenure: tableConfig.default_loan_tenure?.toString(),
    },
  );

  const renderTable = () => {
    switch (vertical) {
      case 'CREDIT_CARD':
      case 'PERSONAL_LOANS':
      case 'INVESTMENTS':
      case 'BANKING':
      case 'PERSONAL_FINANCE':
      case 'TELCO':
      case 'CAR_INSURANCE':
      case 'TRAVEL_INSURANCE':
        return (
          <div className={mobileVisible}>
            <ComparisonTableWrapper
              tableConfig={tableConfig}
              vertical={vertical}
              products={products}
              tabs={(metadata?.tabs ?? []).map((t) => t.label ?? '')}
              slug={slug}
              loanSearchParams={loanSearchParams}
              blockIndex={blockIndex}
              pageType={pageType}
              sortByApprovalOdds={metadata?.sortByApprovalOdds}
            />
          </div>
        );

      /*case 'Travel Insurance':
      return (
        <div className={mobileVisible}>
          <ProductComparisonTableTravelInsurance
            __typename="ComponentPagesComponentSelectedProducts"
            id={tableConfig.id}
            vertical={vertical}
            products={products}
            titleReq={tableConfig.titleReq}
            visible={tableConfig.visible}
            visible_mobile={tableConfig.visible_mobile}
            attributes={tableConfig.attributes}
            blockIndex={blockIndex}
          />
        </div>
      );*/

      default:
        return (
          <div className={mobileVisible}>
            <ProductComparisonTable
              tableConfig={{
                titleReq: tableConfig.titleReq,
                visible_mobile: tableConfig.visible_mobile,
                attributes: tableConfig.attributes,
              }}
              products={products}
            />
          </div>
        );
    }
  };

  return renderTable();
};

// A block's products can belong to another vertical than the page's (CMS-1987).
const ComparisonTableMap = withVerticalScope(
  ComparisonTableMapSwitch,
  (props) => getProductBlockVertical(props.metadata?.product_card),
);

export default ComparisonTableMap;
